import MuleBadge from './MuleBadge';
import React from 'react';
import { MapPinIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Tooltip } from './ui/Tooltip';

interface DriverCardProps {
  driver: any;
  onClick: () => void;
  index: number;
  isSelected?: boolean;
  suggestionScore?: number;
}

/**
 * Format a timestamp as a relative time string (e.g., "5 minutes ago")
 */
const formatRelativeTime = (timestamp: string | undefined): string => {
  if (!timestamp) return '';

  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  } catch (e) {
    return '';
  }
};

export const DriverCard: React.FC<DriverCardProps> = ({ driver, onClick, index, isSelected = false, suggestionScore }) => (
  <div
    key={index}
    onClick={onClick}
    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
      isSelected
        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30'
        : suggestionScore != null
        ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30'
        : driver.isQualified
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30'
        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30'
    }`}
  >
    {/* Header */}
    <div className="flex items-start justify-between mb-2">
      <div className="flex-1">
        <h4 className="font-semibold text-sm text-heading">
          {driver.driverName}
        </h4>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Unit: {driver.unitNumber}<MuleBadge isMule={driver.isMule || driver.attributes?.isMule} />
          </p>
          {suggestionScore != null && (
            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded-full ${
              suggestionScore >= 0.7
                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                : suggestionScore >= 0.5
                ? 'bg-flag text-flag-fg'
                : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
            }`}>
              {Math.round(suggestionScore * 100)}% match
            </span>
          )}
        </div>
        {driver.dispatcher && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Dispatcher: {driver.dispatcher}
          </p>
        )}
      </div>

      {/* Qualification Icons */}
      <div className="flex flex-wrap gap-1">
        {driver.attributes?.isDaycab && (
          <Tooltip content="Daycab Qualified" placement="top" zIndex={999999}>
            <span className="gw-icon gw-icon-daycab text-blue-700 cursor-help" style={{ fontSize: '14px' }}></span>
          </Tooltip>
        )}
        {driver.attributes?.isHazmat && (
          <Tooltip content="Hazmat Certified" placement="top" zIndex={999999}>
            <span className="gw-icon gw-icon-hazmat text-orange-500 cursor-help" style={{ fontSize: '14px' }}></span>
          </Tooltip>
        )}
        {driver.attributes?.isTank && (
          <Tooltip content="Tank Endorsed" placement="top" zIndex={999999}>
            <span className="gw-icon gw-icon-tank text-purple-600 cursor-help" style={{ fontSize: '14px' }}></span>
          </Tooltip>
        )}
        {driver.attributes?.is93k && (
          <Tooltip content="93K Permitted" placement="top" zIndex={999999}>
            <span className="gw-icon text-heading cursor-help" style={{ fontSize: '14px' }}>&#xE9E5;</span>
          </Tooltip>
        )}
        {driver.attributes?.isLightweight && (
          <Tooltip content="< 15K lbs Truck Weight" placement="top" zIndex={999999}>
            <span className="relative inline-flex items-center justify-center cursor-help" style={{ width: '14px', height: '14px' }}>
              <span className="flaticon flaticon-weight text-green-600" style={{ fontSize: '14px' }}></span>
              <span className="absolute text-white font-bold" style={{ fontSize: '4px', bottom: '1px', right: '2px' }}>&lt;15K</span>
            </span>
          </Tooltip>
        )}
        {driver.attributes?.hasPpe && (
          <Tooltip content="PPE Equipped" placement="top" zIndex={999999}>
            <ShieldCheckIcon className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400 cursor-help" />
          </Tooltip>
        )}
      </div>
    </div>

    {/* Distance and Dispatch Status Badges */}
    <div className="flex flex-wrap gap-2 mb-2">
      {driver.distanceFromOrigin !== null && (
        <span
          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
            driver.distanceFromOrigin < 25
              ? 'bg-ok text-ok-fg'
              : driver.distanceFromOrigin < 100
              ? 'bg-info text-info-fg'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
          }`}
        >
          <MapPinIcon className="w-3 h-3 mr-1" />
          {Math.round(driver.distanceFromOrigin)} mi away
          {driver.location?.time && (
            <span className="ml-1 opacity-75">
              ({formatRelativeTime(driver.location.time)})
            </span>
          )}
        </span>
      )}

      {/* Dispatch Status Badge */}
      {driver.dispatchInfo && (
        <span
          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
            driver.dispatchInfo.isCompleted
              ? 'bg-ok text-ok-fg'
              : 'bg-flag text-flag-fg'
          }`}
          title={driver.dispatchInfo.isCompleted
            ? 'Driver is available for new dispatch'
            : `Currently on PRO ${driver.dispatchInfo.proNumber}: ${driver.dispatchInfo.originName} → ${driver.dispatchInfo.destinationName}`
          }
        >
          {driver.dispatchInfo.isCompleted ? '✓ Available' : `⏱ On Load #${driver.dispatchInfo.proNumber}`}
        </span>
      )}

      {/* HOS Drive Time Badge */}
      {driver.driveTimeRemaining && (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-info text-info-fg">
          HOS Drive Time: {driver.driveTimeRemaining} remaining
        </span>
      )}
    </div>

    {/* Out of Service Alert */}
    {driver.outOfServiceInfo && (
      <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-800 dark:text-red-200">
        <strong>Out of Service:</strong> {driver.outOfServiceInfo}
      </div>
    )}

    {/* Current Location */}
    {driver.currentLocation && (
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
        📍 {driver.currentLocation}
      </p>
    )}

    {/* ETA */}
    {driver.eta && (
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
        🕒 ETA: {driver.eta}
      </p>
    )}

    {/* Missing Requirements Warning */}
    {!driver.isQualified && driver.missingRequirements && driver.missingRequirements.length > 0 && (
      <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
        <p className="font-medium text-yellow-800 dark:text-yellow-300 mb-1">
          ⚠️ Missing Requirements:
        </p>
        <ul className="list-disc list-inside text-yellow-800 dark:text-yellow-300 space-y-0.5">
          {driver.missingRequirements.map((req: string, i: number) => (
            <li key={i}>{req}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);
